/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { Button, FlexContainer, theme } from "@nordcloud/gnui";
import { EstateV2Filter } from "~/generated/graphql";
import { ExpandableTag } from "~/components";
import { QueryStateFilter } from "~/hooks";
import { isNotEmpty, noop } from "~/tools";

type Props = {
  filters: QueryStateFilter<EstateV2Filter>[];
  allowSavingFilters: boolean;
  onRemoveKey: (key: (keyof EstateV2Filter)[]) => void;
  onRemoveValue: (key: string, index: number) => void;
  onClear?: () => void;
  onSave?: () => void;
};

export function EstateActiveFilters({
  filters,
  allowSavingFilters,
  onRemoveKey,
  onRemoveValue,
  onClear = noop,
  onSave = noop,
}: Props) {
  const showActions = isNotEmpty(filters);

  const handleRemoveKey = (key: keyof EstateV2Filter) => {
    switch (key) {
      case "waste":
        onRemoveKey(["wastePolicy", key]);
        break;
      case "enrichment":
        onRemoveKey(["enrichmentIntegrationId", key]);
        break;
      case "provider":
        onRemoveKey(["externalIntegration", "region", key]);
        break;
      default:
        onRemoveKey([key]);
        break;
    }
  };

  return (
    <FlexContainer justifyContent="space-between">
      <FlexContainer wrap="wrap">
        <span css={{ marginRight: theme.spacing.spacing02 }}>
          Applied filters:
        </span>
        {filters.map(([key, value]) => (
          <ExpandableTag
            key={key}
            name={key}
            value={value}
            onRemoveKey={() => handleRemoveKey(key)}
            onRemoveValue={onRemoveValue}
          />
        ))}
      </FlexContainer>

      <When condition={showActions}>
        <FlexContainer gap={theme.spacing.spacing04}>
          <Button icon="filterClear" severity="low" onClick={onClear}>
            Clear Filters
          </Button>
          <When condition={allowSavingFilters}>
            <Button icon="filterSelect" severity="low" onClick={onSave}>
              Save Filters
            </Button>
          </When>
        </FlexContainer>
      </When>
    </FlexContainer>
  );
}
